/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import * as color from '@constant/color';
import React from 'react';

import IconBlock from '@component/common/block/icon-block';
import { useRef, useState, useEffect } from 'react';

interface IProps {
  children?: React.ReactNode;
  width?: string;
  height?: string;
  maxHeight?: string;
  backgroundColor?: string;
  padding?: {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  };
  direction: 'x' | 'y';
  onScroll?: React.UIEventHandler<HTMLDivElement>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  border?:
    | {
        width?: string;
        color?: string;
        radius?: string;
      }
    | 'none';
  showScrollIndicator?: boolean;
}

const Wrapper = styled.div<{
  $height: string;
  $maxHeight?: string;
  $backgroundColor: string;
  $border: IProps['border'];
}>`
  position: relative;
  width: 100%;
  height: ${(props) => props.$height};
  max-height: ${(props) => props.$maxHeight || 'none'};
  background-color: ${(props) => props.$backgroundColor};
  display: flex;
  flex-direction: column;

  /* border */
  ${(props) => {
    if (props.$border === 'none') {
      return css`
        border: none;
        border-radius: 0;
      `;
    }

    return css`
      border: ${props.$border?.width || '0'} solid ${props.$border?.color || color.GRAY_1};
      border-radius: ${props.$border?.radius || '0px'};
    `;
  }}
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 0;

  flex-flow: ${(props: IProps) => (props.direction === 'x' ? 'row nowrap' : 'column nowrap')};
  overflow-x: ${(props: IProps) => (props.direction === 'x' ? 'auto' : 'hidden')};
  overflow-y: ${(props: IProps) => (props.direction === 'y' ? 'auto' : 'hidden')};
  width: ${(props) => props.width};
  /* height handled by flex */
  margin: 0 auto;

  /* border */
  ${(props) => {
    if (props.border === 'none') {
      return css`
        border: none;
        border-radius: 0;
      `;
    }

    return css`
      border: ${props.border?.width || '0'} solid ${props.border?.color || color.GRAY_1};
      border-radius: ${props.border?.radius || '0px'};
    `;
  }} /* scroll bar hidden
  - firefox
  - ie
  - chrome, safari, edge
  */ scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const DownIndicatorContainer = styled.div`
  position: absolute;
  bottom: 0px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;

  display: flex;
  justify-content: center;
  align-items: center;

  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${color.WHITE_1};
  box-shadow: ${color.SURFACE_SHADOW};
  opacity: 0.9;

  cursor: pointer;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 1;
  }
`;

const UpIndicatorContainer = styled.div`
  position: absolute;
  top: 0px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;

  display: flex;
  justify-content: center;
  align-items: center;

  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${color.WHITE_1};
  box-shadow: ${color.SURFACE_SHADOW};
  opacity: 0.9;

  cursor: pointer;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 1;
  }
`;

const INDICATOR_ICON_DESIGN = {
  icon: { size: 'medium', color: color.GRAY_2 },
  box: { event: { cursor: 'pointer' } },
} as const;

const Scroll: React.FC<IProps> = ({
  children,
  direction,
  width = '100%',
  height = 'auto',
  maxHeight,
  backgroundColor = 'none',
  border = 'none',
  padding = { top: '0px', bottom: '0px', left: '0px', right: '0px' },
  onScroll,
  showScrollIndicator = false,
  containerRef,
}) => {
  const innerScrollRef = useRef<HTMLDivElement>(null);
  const scrollRef = (containerRef ?? innerScrollRef) as React.RefObject<HTMLDivElement>;
  const [showDownIndicator, setShowDownIndicator] = useState(false);
  const [showUpIndicator, setShowUpIndicator] = useState(false);

  const checkOverflow = () => {
    const el = scrollRef.current;
    if (!el) return;

    const isScrollable = el.scrollHeight > el.clientHeight;
    const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 5;
    const isAtTop = el.scrollTop <= 5;

    setShowDownIndicator(isScrollable && !isAtBottom);
    setShowUpIndicator(isScrollable && !isAtTop);
  };

  useEffect(() => {
    if (!showScrollIndicator) return;

    const el = scrollRef.current;
    if (!el) return;

    checkOverflow();

    const handleScroll = () => {
      checkOverflow();
    };

    const handleResize = () => {
      checkOverflow();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
    }
    el.addEventListener('scroll', handleScroll);

    const observer = new ResizeObserver(() => {
      checkOverflow();
    });
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
      observer.disconnect();
    };
  }, [children, height, maxHeight, showScrollIndicator]);

  const handleDownIndicatorClick = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ top: 100, behavior: 'smooth' });
  };

  const handleUpIndicatorClick = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ top: -100, behavior: 'smooth' });
  };

  return (
    <Wrapper $height={height} $maxHeight={maxHeight} $backgroundColor={backgroundColor} $border={border}>
      <div
        css={css`
          height: ${padding.top || '0px'};
          flex-shrink: 0;
        `}
      />
      <Container
        ref={scrollRef}
        direction={direction}
        width={width}
        // height removed, relying on flex
        padding={padding}
        css={css`
          padding-left: ${padding.left || '0px'};
          padding-right: ${padding.right || '0px'};
        `}
        onScroll={(e) => {
          if (onScroll) onScroll(e);
          if (showScrollIndicator) checkOverflow();
        }}>
        {children}
      </Container>
      <div
        css={css`
          height: ${padding.bottom || '0px'};
          flex-shrink: 0;
        `}
      />
      {showScrollIndicator && showUpIndicator && (
        <UpIndicatorContainer onClick={handleUpIndicatorClick}>
          <IconBlock icon='up' design={INDICATOR_ICON_DESIGN} />
        </UpIndicatorContainer>
      )}
      {showScrollIndicator && showDownIndicator && (
        <DownIndicatorContainer onClick={handleDownIndicatorClick}>
          <IconBlock icon='down' design={INDICATOR_ICON_DESIGN} />
        </DownIndicatorContainer>
      )}
    </Wrapper>
  );
};

// flex: 1 — 부모(display: flex)의 남은 영역을 채움 (height 지정 시 height 우선)
// min-height: 0 — 최소 높이를 0으로 설정하여 flex 배정 크기까지 줄어들 수 있게 함 (기본값 auto면 최소 높이 = 콘텐츠 높이라서 스크롤 불가)
const ScrollContainer = styled.div<{ height?: string; width?: string }>`
  ${(props) => (props.height ? `height: ${props.height};` : 'flex: 1;')}
  width: ${(props) => props.width ?? 'auto'};
  min-height: 0;
  display: flex;
  flex-flow: column nowrap;
`;

export { ScrollContainer };
export default Scroll;
